import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { REGISTER } from '@/constants/testIds';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function Signup() {
    const { signupWithEmail } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const redirectTo = searchParams.get('redirect') || '/dashboard';
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!agreed) {
            setError('Please agree to the Terms of Service and Privacy Policy to continue.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await signupWithEmail(name, email, password);
            toast.success('Account created. Let\'s plan something!');
            navigate(redirectTo, { replace: true });
        } catch (err) {
            setError(err?.response?.data?.detail || 'Signup failed');
        } finally {
            setLoading(false);
        }
    };

    const googleSignup = () => {
        if (!agreed) {
            setError('Please agree to the Terms of Service and Privacy Policy to continue.');
            return;
        }
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + redirectTo;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen bg-[var(--ns-bg)] flex">
            <div className="hidden lg:flex flex-1 relative overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/17319005/pexels-photo-17319005.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
                    alt="Kyoto"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(29,45,68,0.55), rgba(224,122,95,0.30))' }} />
                <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
                    <div className="flex items-center gap-2">
                        <span className="w-10 h-10 rounded-2xl flex items-center justify-center bg-white/15 backdrop-blur">
                            <MapPin size={18} />
                        </span>
                        <span className="font-display text-2xl font-semibold">NextStop</span>
                    </div>
                    <div className="max-w-md">
                        <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-tight tracking-tight">Group trips, finally aligned.</h1>
                        <p className="mt-4 text-white/80 text-lg leading-relaxed">Save inspiration, vote on plans, and build itineraries everyone agrees on. No more endless group chats.</p>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'var(--ns-navy)' }}>
                            <MapPin size={18} className="text-white" />
                        </span>
                        <span className="font-display text-2xl font-semibold">NextStop</span>
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Create your account</h2>
                    <p className="text-[var(--ns-text2)] mt-2">Start planning your next adventure in minutes.</p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <Label htmlFor="name" className="text-sm text-[var(--ns-text2)]">Full name</Label>
                            <Input
                                id="name"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Alex Rivera"
                                data-testid={REGISTER.nameInput}
                                className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white"
                            />
                        </div>
                        <div>
                            <Label htmlFor="email" className="text-sm text-[var(--ns-text2)]">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                data-testid={REGISTER.emailInput}
                                className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-sm text-[var(--ns-text2)]">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                required
                                minLength={8}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="At least 8 characters"
                                data-testid={REGISTER.passwordInput}
                                className="h-12 rounded-xl mt-1 border-[var(--ns-border)] bg-white"
                            />
                        </div>
                        <div className="flex items-start gap-2.5 pt-1">
                            <Checkbox
                                id="agree-terms"
                                checked={agreed}
                                onCheckedChange={(v) => setAgreed(!!v)}
                                data-testid="signup-terms-checkbox"
                                className="mt-0.5"
                            />
                            <Label
                                htmlFor="agree-terms"
                                className="text-xs text-[var(--ns-text2)] leading-relaxed cursor-pointer font-normal"
                            >
                                I agree to the{' '}
                                <Link
                                    to="/terms"
                                    data-testid="signup-terms-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--ns-clay)] hover:underline"
                                >
                                    Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link
                                    to="/privacy-policy"
                                    data-testid="signup-privacy-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[var(--ns-clay)] hover:underline"
                                >
                                    Privacy Policy
                                </Link>.
                            </Label>
                        </div>
                        {error && (
                            <div data-testid={REGISTER.errorAlert} className="text-sm text-[var(--ns-clayHover)] bg-[var(--ns-clay)]/10 border border-[var(--ns-clay)]/30 rounded-xl px-4 py-3">
                                {error}
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={loading || !agreed}
                            data-testid={REGISTER.submitButton}
                            className="w-full h-12 rounded-full font-medium text-white disabled:opacity-50"
                            style={{ background: 'var(--ns-navy)' }}
                        >
                            {loading ? 'Creating account…' : 'Create account'}
                        </Button>
                    </form>

                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[var(--ns-border)]"></span></div>
                        <div className="relative flex justify-center text-xs uppercase tracking-widest"><span className="bg-[var(--ns-bg)] px-3 text-[var(--ns-text3)]">or</span></div>
                    </div>

                    <Button
                        type="button"
                        onClick={googleSignup}
                        data-testid={REGISTER.googleButton}
                        variant="outline"
                        className="w-full h-12 rounded-full font-medium border-[var(--ns-border)] hover:bg-[var(--ns-soft)]"
                    >
                        <svg className="mr-2" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg"><path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" fill="#4285F4"/><path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.94v2.32A9 9 0 009 18z" fill="#34A853"/><path d="M3.97 10.72A5.4 5.4 0 013.68 9c0-.6.1-1.18.29-1.72V4.96H.94A9 9 0 000 9c0 1.45.35 2.83.94 4.04l3.03-2.32z" fill="#FBBC05"/><path d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 00.94 4.96l3.03 2.32C4.68 5.16 6.66 3.58 9 3.58z" fill="#EA4335"/></svg>
                        Continue with Google
                    </Button>

                    <p className="mt-8 text-sm text-[var(--ns-text2)] text-center">
                        Already on NextStop?{' '}
                        <Link
                            to={`/login${redirectTo !== '/dashboard' ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`}
                            data-testid={REGISTER.loginLink}
                            className="font-medium text-[var(--ns-navy)] hover:text-[var(--ns-clay)] transition"
                        >Sign in</Link>
                    </p>
                    <div className="mt-6 flex items-center justify-center gap-5 text-xs text-[var(--ns-text3)]" data-testid="auth-footer-links">
                        <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-[var(--ns-clay)]">Privacy</Link>
                        <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[var(--ns-clay)]">Terms</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
